import './NewSidebar.scss';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { setSidebarMobileOpen } from '../../storeRedux';
import { operatorMenu, teamleadMenu, partnerMenu } from './menuConfig';
import SidebarGroup from './SidebarGroup';
import { showAlert } from '../AlertBox/AlertBox';
import { PowerIcon, ArrowRightOnRectangleIcon, XMarkIcon, GlobeIcon } from './icons';
import { LANGUAGES } from '../../i18n';
import LanguageModal from '../LanguageModal/LanguageModal';

// Boost icon inline — matches sidebar style
const BoltIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
  </svg>
);

export default function NewSidebar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const mobileOpen = useSelector((state) => state.SIDEBAR_MOBILE_OPEN);
  const userRole = useSelector((state) => state.USER_ROLE);
  const isUserActive = useSelector((state) => state.IS_ACTIVE_USER);
  const roleIncludeBoost = useSelector((state) => state.USER_ROLE_INCLUDE_BOOST);
  const payoutSettingsEnabled = useSelector((state) => state.PAYOUT_SETTINGS_ENABLED);
  const clientSidebarName = useSelector((state) => state.CLIENT_SIDEBAR_NAME);
  const clientSidebarSubtitle = useSelector((state) => state.CLIENT_SIDEBAR_SUBTITLE);
  const userName = useSelector((state) => state.USER_NAME);
  const usdtBalance = useSelector((state) => state.USDT_BALANCE);
  const apiUrl = useSelector((state) => state.API_URL);
  const apiUrlNoRa = useSelector((state) => state.API_URL_NO_RAN);
  const nextBoostTime = useSelector((state) => state.BOOST_ACCOUNT_NEXT_TIME);
  const theme = useSelector((state) => state.APP_THEME);
  const rates = useSelector((state) => state.RATES);

  const [statusUpdated, setStatusUpdated] = useState(false);
  const [boostTimeLeft, setBoostTimeLeft] = useState('');
  const [boostIsWorking, setBoostIsWorking] = useState(false);
  const [langModalOpen, setLangModalOpen] = useState(false);

  const menuGroups = userRole === 'teamlead' ? teamleadMenu
                   : userRole === 'partner' ? partnerMenu
                   : operatorMenu;

  const closeMobile = () => {
    dispatch(setSidebarMobileOpen(false));
  };

  const handleExit = async () => {
    const savedLang = localStorage.getItem('i18nextLng');
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        await fetch(`${apiUrl}token/logout/`, {
          method: 'POST',
          headers: { Authorization: `JWT ${token}` },
        });
      } catch (_) {
        // Local cleanup still protects the browser when the network is down.
      }
    }
    navigate('/auth');
    localStorage.clear();
    if (savedLang) {
      localStorage.setItem('i18nextLng', savedLang);
    }
  };

  const changeUserActiveStatus = async () => {
    const jwtToken = localStorage.getItem('accessToken');
    try {
      const response = await fetch(apiUrlNoRa + 'api/v3/user/disable/', {
        method: 'POST',
        headers: {
          'Authorization': `JWT ${jwtToken}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      await response.json();
      setStatusUpdated(true);
    } catch (error) {
      showAlert('error', t('layout:sidebar.errors.statusChangeFailed'));
    }
  };

  // Boost timer
  useEffect(() => {
    const update = () => {
      if (!nextBoostTime) { setBoostIsWorking(false); setBoostTimeLeft(''); return; }
      const diff = Math.floor(new Date(nextBoostTime).getTime() / 1000) - Math.floor(Date.now() / 1000);
      if (diff <= 0) { setBoostIsWorking(false); setBoostTimeLeft(''); return; }
      setBoostIsWorking(true);
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setBoostTimeLeft(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`);
    };
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [nextBoostTime]);

  const startBoost = async () => {
    const jwtToken = localStorage.getItem('accessToken');
    try {
      const response = await fetch(apiUrlNoRa + 'api/v3/user/boost/', {
        method: 'POST',
        headers: { 'Authorization': `JWT ${jwtToken}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (response.status === 200 || response.status === 409) {
        if (data.next_allowed_time) {
          dispatch({ type: 'app/setNextTimeOfBoostAccount', payload: data.next_allowed_time });
        }
      }
      showAlert(response.status === 200 ? 'success' : 'error', data.message);
    } catch (error) {
      showAlert('error', t('layout:sidebar.errors.boostError'));
    }
  };

  useEffect(() => {
    if (statusUpdated) {
      if (isUserActive || payoutSettingsEnabled) {
        showAlert('success', t('layout:sidebar.errors.statusChangeSuccess'), () => { setStatusUpdated(false); });
      } else {
        showAlert('error', t('layout:sidebar.errors.addRequisitesOrPayout'), () => { setStatusUpdated(false); });
      }
    }
  }, [statusUpdated, isUserActive, payoutSettingsEnabled, t]);

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  return (
    <>
      {mobileOpen && <div className="NewSidebar__overlay" onClick={closeMobile} />}

      <aside className={`NewSidebar ${theme === 'light' ? 'theme--light' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        {/* Header */}
        <div className="NewSidebar__header">
          <div className="NewSidebar__brand">
            <span className="NewSidebar__brand-name">{clientSidebarName}</span>
            <span className="NewSidebar__brand-subtitle">{clientSidebarSubtitle}</span>
          </div>
          <div className="NewSidebar__brand-collapsed">
            <span className="NewSidebar__brand-initial">{clientSidebarName?.charAt(0) || 'M'}</span>
          </div>
          <button className="NewSidebar__mobile-close" onClick={closeMobile}>
            <XMarkIcon />
          </button>
        </div>

        {/* User info */}
        <div className="NewSidebar__user">
          <div className={`NewSidebar__user-avatar${userRole === 'teamlead' ? ' NewSidebar__user-avatar--tl' : ''}`}>
            {userName?.charAt(0)?.toUpperCase() || 'U'}
            {userRole === 'teamlead' && <span className="NewSidebar__user-avatar-badge">TL</span>}
          </div>
          <div className="NewSidebar__user-info">
            <span className="NewSidebar__user-name">{userName}</span>
            <span className="NewSidebar__user-balance">{usdtBalance} $</span>
          </div>
        </div>

        {/* Rates (visible on mobile & hover) */}
        {rates && rates.length > 0 && (
          <div className="NewSidebar__rates">
            {rates.map((item, index) => (
              <span key={index} className="NewSidebar__rate">{item.code}: {item.rate}</span>
            ))}
          </div>
        )}

        {/* Menu groups */}
        <nav className="NewSidebar__nav">
          {menuGroups.map((group) => (
            <SidebarGroup
              key={group.groupLabelKey}
              group={group}
              collapsed={false}
              onMobileClose={closeMobile}
            />
          ))}
        </nav>

        {/* Bottom actions */}
        <div className="NewSidebar__bottom">
          {/* Boost for operators */}
          {userRole === 'operator' && roleIncludeBoost === 'yes' && (
            <button
              className={`NewSidebar__action-btn action--boost ${boostIsWorking ? 'boost--active' : ''}`}
              onClick={startBoost}
              title="Boost"
            >
              <BoltIcon />
              <span>{boostIsWorking ? boostTimeLeft : 'Boost'}</span>
            </button>
          )}

          {/* Language selector */}
          <button
            className="NewSidebar__action-btn"
            onClick={() => setLangModalOpen(true)}
            title={t('layout:sidebar.actions.language')}
          >
            <GlobeIcon />
            <span>{currentLang.flag} {currentLang.label}</span>
          </button>

          {/* Status toggle */}
          {userRole === 'operator' && (isUserActive || payoutSettingsEnabled) && (
            <button
              className={`NewSidebar__action-btn ${(isUserActive || payoutSettingsEnabled) ? 'status--online' : 'status--offline'}`}
              onClick={changeUserActiveStatus}
              title={t('layout:sidebar.actions.finishWork')}
            >
              <PowerIcon />
              <span>{t('layout:sidebar.actions.finishWork')}</span>
            </button>
          )}

          {/* Exit */}
          <button className="NewSidebar__action-btn action--exit" onClick={handleExit} title={t('layout:sidebar.actions.exit')}>
            <ArrowRightOnRectangleIcon />
            <span>{t('layout:sidebar.actions.exit')}</span>
          </button>
        </div>
      </aside>

      <LanguageModal isOpen={langModalOpen} onClose={() => setLangModalOpen(false)} />
    </>
  );
}
